import { isInternalUser } from '@steerfi/shared';
import { FileText as FileTextIcon } from 'lucide-react';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useSupplierDisplay } from '@/contexts/supplier-display-context';
import { createSortMapper, SUPPLIERS_SORT_MAP } from '@/lib/sort-mapping';
import { formatDate, formatDateTime } from '@/lib/utils';
import {
  type ColumnConfig,
  DataTable,
  type DataTableResponse,
  type FilterConfig,
} from './data-table';
import type { RowActivatorLink } from './row-activator';
import { SupplierStatus } from './status-chip';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

export const DEFAULT_SUPPLIER_SORT_BY = 'created_at';

const sortMapper = createSortMapper(SUPPLIERS_SORT_MAP);

/**
 * A `sortBy` arriving from the URL, or `fallback` when the grid cannot honour
 * it.
 *
 * `GET /api/parties/suppliers` 400s a value outside its enum, which the grid
 * can only render as an error state, so a stale or hand-edited link has to be
 * checked before it reaches a request.
 *
 * Checked against this grid's own map rather than the wider API enum: the API
 * still accepts keys with no column here (`verification_status`, say, which is
 * no longer sortable), and honouring one would order the rows by a column
 * showing no arrow at all.
 */
export function readSupplierSortBy(raw: string | null | undefined, fallback: string): string {
  const value = raw?.trim() ?? '';
  return sortMapper.toColumn(value) ? value : fallback;
}

function NotesCountBadge({ count }: { count: number }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <FileTextIcon size={16} />
      <span
        style={{
          position: 'absolute',
          top: '-6px',
          right: '-8px',
          minWidth: '16px',
          height: '16px',
          padding: '0 4px',
          borderRadius: '999px',
          backgroundColor: 'var(--foreground)',
          color: 'var(--background)',
          fontSize: '10px',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        {count}
      </span>
    </span>
  );
}

// Supplier interface based on the existing mock data structure
export interface Supplier {
  id: string;
  name: string;
  clientName?: string | null;
  preferredPaymentMethod?: string | null;
  email: string;
  verificationStatus?: string | null;
  externalRef?: string | null;
  relationshipApproval?: string | null;
  relationshipApprovalAt?: string | null;
  createdAt?: string;
  createdByName?: string | null;
  notes_count?: number;
  [key: string]: unknown;
}

interface Props {
  data?: DataTableResponse<Supplier> | undefined;
  isLoading?: boolean;
  error?: string | null;
  onSupplierClick?: (supplier: Supplier) => void;
  /** Detail route for the row's keyboard activator; omit to fall back to a button. */
  getRowLink?: ((supplier: Supplier) => RowActivatorLink) | undefined;
  onFilterChange?: (filterKey: string, value: string | string[]) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  /** Sort actually in effect, so the header arrow reflects the query. */
  currentSortKey?: string;
  currentSortOrder?: 'asc' | 'desc';
  onPageChange?: (page: number) => void;
  searchInput?: React.ReactNode;
  /** Override for the external ref column label (from party setting external_ref_display_name) */
  externalRefLabel?: string;
  /** Current filter values so dropdowns show the right selection */
  currentFilterValues?: Record<string, string | string[]>;
}

export const SuppliersTable = ({
  data,
  isLoading = false,
  error = null,
  onSupplierClick,
  getRowLink,
  onFilterChange,
  onSortChange,
  currentSortKey,
  currentSortOrder,
  onPageChange,
  searchInput,
  externalRefLabel = 'External Ref',
  currentFilterValues,
}: Props) => {
  const { user } = useKeycloakAuth();
  const showClientName = isInternalUser(user?.roles);
  // This record is called "supplier", "beneficiary" or a tenant's own word, and
  // the page heading already follows that setting -- so the table's name and
  // its row labels have to as well, or a screen reader announces "beneficiary"
  // under a heading that says Suppliers.
  const { labels } = useSupplierDisplay();

  const columns: ColumnConfig<Supplier>[] = [
    {
      key: 'externalRef',
      label: externalRefLabel,
      sortable: false,
      render: (item) => (
        <span className="text-muted-foreground font-mono text-sm">{item.externalRef ?? '—'}</span>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (item) => <span className="font-medium">{item.name}</span>,
    },
    {
      // A beneficiary can belong to several clients, so this cell is a joined
      // list built per row -- there is no single column to order by.
      key: 'clientName',
      label: 'Client',
      sortable: false,
      render: (item) => <span className="text-muted-foreground">{item.clientName || '—'}</span>,
    },
    {
      // Resolved per row through a fallback chain (supplier setting, party
      // setting, then primary account type), so no column holds what's shown.
      key: 'preferredPaymentMethod',
      label: 'Preferred Payment',
      sortable: false,
      render: (item) => (
        <span className="text-muted-foreground">{item.preferredPaymentMethod || '—'}</span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (item) => <span className="text-muted-foreground">{item.email}</span>,
    },
    {
      // Not sortable: "Verified" / "Partially verified" / "Unverified" is
      // computed from three separate verification checks (ACH, OFAC, USPS) with
      // a different rule for check and digital-wallet beneficiaries. The
      // backend can only order by the raw latest status, which is a different
      // value than the one shown here.
      key: 'verificationStatus',
      label: 'Verification Status',
      sortable: false,
      render: (item) => (
        <span className="text-muted-foreground">{item.verificationStatus ?? '—'}</span>
      ),
    },
    {
      key: 'relationshipApproval',
      label: 'Approval',
      sortable: true,
      render: (item) => {
        const approval = item.relationshipApproval;
        const label =
          approval === 'APPROVED'
            ? 'Approved'
            : approval === 'DENIED'
              ? 'Denied'
              : approval === 'IN_PROGRESS'
                ? 'In progress'
                : 'Awaiting Approval';
        const approvalAt = item.relationshipApprovalAt;
        const showTimestamp = (approval === 'APPROVED' || approval === 'DENIED') && approvalAt;
        if (showTimestamp) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground cursor-default">{label}</span>
              </TooltipTrigger>
              <TooltipContent>
                {approval === 'APPROVED' ? 'Approved' : 'Denied'} on {formatDateTime(approvalAt)}
              </TooltipContent>
            </Tooltip>
          );
        }
        return <span className="text-muted-foreground">{label}</span>;
      },
    },
    {
      key: 'createdByName',
      label: 'Created By',
      sortable: true,
      render: (item) => <span className="text-muted-foreground">{item.createdByName ?? '—'}</span>,
    },
    {
      key: 'notes_count',
      label: 'Notes',
      sortable: false,
      render: (item) => {
        const count = item.notes_count;
        if (!count || count === 0) return null;
        return <NotesCountBadge count={count} />;
      },
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      align: 'right',
      render: (item) => (
        <span className="text-muted-foreground">
          {item.createdAt ? formatDate(item.createdAt) : '—'}
        </span>
      ),
    },
  ];

  const visibleColumns = showClientName
    ? columns
    : columns.filter((column) => column.key !== 'clientName');

  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      placeholder: 'Select statuses',
      multiSelect: true,
      options: [
        { value: SupplierStatus.ACTIVE, label: 'Active' },
        { value: SupplierStatus.INACTIVE, label: 'Inactive' },
        { value: SupplierStatus.PENDING_VERIFICATION, label: 'Pending Verification' },
        { value: SupplierStatus.PROSPECT_PENDING, label: 'Prospect Pending' },
      ],
    },
    {
      key: 'relationshipApproval',
      label: 'Approval',
      placeholder: 'All approvals',
      multiSelect: true,
      options: [
        { value: 'APPROVED', label: 'Approved' },
        { value: 'DENIED', label: 'Denied' },
        { value: 'NULL', label: 'Awaiting Approval' },
        { value: 'IN_PROGRESS', label: 'In progress' },
      ],
    },
    {
      key: 'preferredPaymentMethod',
      label: 'Preferred Payment',
      placeholder: 'All methods',
      multiSelect: true,
      options: [
        { value: 'ACH', label: 'ACH' },
        { value: 'DIGITAL_WALLET', label: 'Digital wallet' },
        { value: 'CHECK', label: 'Check' },
      ],
    },
    {
      key: 'createdByFilter',
      label: 'Created by',
      placeholder: 'Anyone or only me',
      options: [{ value: 'me', label: 'Only me' }],
    },
  ];

  return (
    <DataTable
      data={data}
      columns={visibleColumns}
      caption={labels.plural}
      filters={filters}
      currentFilterValues={currentFilterValues ?? {}}
      isLoading={isLoading}
      error={error}
      onRowClick={onSupplierClick}
      // The external ref column can be empty, so the name carries the activator.
      rowActivatorColumnKey="name"
      getRowLabel={(item) => `Open ${labels.singular.toLowerCase()} ${item.name}`}
      {...(getRowLink && { getRowLink })}
      {...(onFilterChange && { onFilterChange })}
      {...(onSortChange && { onSortChange })}
      {...(onPageChange && { onPageChange })}
      extraFilters={searchInput}
      emptyMessage={`No ${labels.plural.toLowerCase()} found`}
      loadingMessage={`Loading ${labels.plural.toLowerCase()}...`}
      // Driven by the page filter state; a default declared here is a second
      // source of truth that starts out disagreeing with the query.
      {...(currentSortKey && { currentSortKey })}
      {...(currentSortOrder && { currentSortOrder })}
    />
  );
};
